/**
 * Shared navigation bar used across all StoryMint pages.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User as UserIcon } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useCredits } from '../../hooks/useCredits';
import { useUserProfile, isCreatorPlus, isActiveSubscriber } from '../../hooks/useUserProfile';
import { BuyCreditsModal } from '../ui/BuyCreditsModal';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';

interface NavbarProps {
  /** "public" = Pricing + Login + Get Started Free (Dashboard if already authed)
   *  "authed" = Credits pill + nav links + avatar dropdown + Log Out */
  variant: 'public' | 'authed';
  /** When provided, the right side renders a simple back link instead of nav items */
  backHref?: string;
  backLabel?: string;
}

/** Returns first name, or initials, or "Account" fallback */
function getDisplayName(displayName: string | null | undefined, email: string | null | undefined): string {
  if (displayName) {
    return displayName.split(' ')[0] ?? displayName;
  }
  if (email) {
    return (email[0] ?? 'A').toUpperCase();
  }
  return 'Account';
}

/**
 * Avatar circle — shows profile photo, Google initial, or generic user icon.
 * isGoogleUser: true  → show first-name initial when no photo
 * isGoogleUser: false → show generic UserIcon when no photo (email/password accounts)
 */
function UserAvatar({
  photoURL,
  name,
  isGoogleUser,
}: {
  photoURL?: string | null;
  name: string;
  isGoogleUser: boolean;
}) {
  if (photoURL) {
    return (
      <img
        src={photoURL}
        alt=""
        aria-hidden="true"
        className="w-7 h-7 rounded-full object-cover select-none"
      />
    );
  }
  if (isGoogleUser) {
    const initial = name[0]?.toUpperCase() ?? '?';
    return (
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/20 text-primary text-xs font-bold select-none"
        aria-hidden="true"
      >
        {initial}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/20 text-primary select-none"
      aria-hidden="true"
    >
      <UserIcon className="w-4 h-4" />
    </span>
  );
}

/**
 * Shared navigation bar used across all StoryMint pages.
 * Single source of truth — never inline nav in page components.
 */
export function Navbar({ variant, backHref, backLabel = '← Dashboard' }: NavbarProps) {
  const { user, isAuthenticated, logout } = useAuth();
  const { data: credits } = useCredits();
  const { data: profile } = useUserProfile();
  const navigate = useNavigate();

  const isCreatorPlusUser = isCreatorPlus(profile);
  const isGoogleUser = user?.providerData[0]?.providerId === 'google.com';
  const displayName = getDisplayName(user?.displayName, user?.email);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const outOfCredits = credits !== undefined && credits.creditBalance === 0;
  const isNewFreeUser =
    outOfCredits &&
    !isActiveSubscriber(profile) &&
    (credits?.lifetimeCreditsPurchased ?? 0) === 0;
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);

  return (
    <>
    <BuyCreditsModal open={buyCreditsOpen} onClose={() => setBuyCreditsOpen(false)} />
    <header className="border-b border-border px-4 sm:px-6 py-4">
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">

        {/* Logo */}
        <Link to="/" className="flex items-center shrink-0">
          <img src="/logo.png" alt="StoryMint" className="h-9 w-auto" />
        </Link>

        {/* Right side — three possible states */}
        {backHref ? (
          /* Back-link variant */
          <Link
            to={backHref}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {backLabel}
          </Link>

        ) : variant === 'authed' ? (
          /* Authenticated app nav */
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Credits pill */}
            {credits !== undefined && (
              <div className="relative">
                <div className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-primary/10 rounded-md">
                  <span className="text-xs sm:text-sm font-medium text-violet-400">
                    {credits.creditBalance}{' '}
                    {credits.creditBalance === 1 ? 'credit' : 'credits'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setBuyCreditsOpen(true)}
                  className="absolute -top-2 -right-2 flex items-center justify-center h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-none hover:bg-primary/80 transition-colors shadow-sm"
                  aria-label="Buy more credits"
                >
                  +
                </button>
              </div>
            )}

            {/* Dashboard link */}
            <Link
              to="/dashboard"
              className="text-xs sm:text-sm text-foreground hover:text-primary transition-colors hidden sm:inline"
            >
              Dashboard
            </Link>

            {/* Pen Persona link */}
            <Link
              to="/persona"
              className="text-xs sm:text-sm text-foreground hover:text-primary transition-colors hidden sm:inline"
            >
              Pen Persona
            </Link>

            {/* Series nav link */}
            <Link
              to="/series"
              className={`text-xs sm:text-sm transition-colors hidden sm:inline ${
                isCreatorPlusUser
                  ? 'text-foreground hover:text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              title={isCreatorPlusUser ? undefined : 'Creator+ feature — click to learn more'}
            >
              🔗 Series
            </Link>

            {/* Upgrade CTA for free users only */}
            {(!profile || !profile.subscriptionTier || profile.subscriptionTier === 'free') && (
              <Link
                to="/pricing"
                className="text-xs sm:text-sm font-semibold text-violet-400 hover:text-violet-300 transition-colors hidden sm:inline"
              >
                Upgrade ✨
              </Link>
            )}

            {/* Mobile-only avatar dropdown — full nav menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="sm:hidden flex items-center focus:outline-none"
                  aria-label="Menu"
                >
                  <UserAvatar photoURL={user?.photoURL} name={displayName} isGoogleUser={isGoogleUser} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/dashboard" className="w-full">Dashboard</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/persona" className="w-full">Pen Persona</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/series" className="w-full">🔗 Series</Link>
                </DropdownMenuItem>
                {(!profile || !profile.subscriptionTier || profile.subscriptionTier === 'free') && (
                  <DropdownMenuItem asChild>
                    <Link to="/pricing" className="w-full font-semibold text-violet-400">Upgrade ✨</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="w-full">Settings</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings#billing" className="w-full">Billing</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => void handleLogout()}
                  className="text-destructive focus:text-destructive"
                >
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Desktop avatar dropdown — Settings / Billing / Logout */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="hidden sm:inline-flex items-center gap-1.5 focus:outline-none"
                  aria-label="Account menu"
                >
                  <UserAvatar photoURL={user?.photoURL} name={displayName} isGoogleUser={isGoogleUser} />
                  {!user?.photoURL && isGoogleUser && (
                    <span className="hidden md:inline truncate max-w-[100px] text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors">
                      {displayName}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="w-full">
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings#billing" className="w-full">
                    Billing
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => void handleLogout()}
                  className="text-destructive focus:text-destructive"
                >
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

          </div>

        ) : (
          /* Public nav: Blog + Pricing + Login + CTA (or Dashboard if already authed) */
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/blog"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden sm:inline"
            >
              Blog
            </Link>
            <Link
              to="/pricing"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden sm:inline"
            >
              Pricing
            </Link>
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden sm:inline"
                >
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
                >
                  Get Started Free
                </Link>
              </>
            )}
          </div>
        )}

      </div>
    </header>

    {variant === 'authed' && outOfCredits && (
      <div className="bg-amber-950/60 border-b border-amber-700/50 px-4 sm:px-6 py-2.5 flex items-center justify-between gap-4">
        <p className="text-xs sm:text-sm text-amber-300">
          {isNewFreeUser
            ? 'Your free preview is complete — upgrade to finish your book and unlock the full experience.'
            : 'You\'re out of credits — add more to keep writing.'}
        </p>
        <div className="flex items-center gap-3 shrink-0">
          <Link
            to="/pricing"
            className="text-xs sm:text-sm font-medium text-amber-300 hover:text-amber-200 transition-colors"
          >
            Upgrade plan
          </Link>
          <button
            type="button"
            onClick={() => setBuyCreditsOpen(true)}
            className="text-xs sm:text-sm font-semibold px-3 py-1 rounded-md bg-amber-600 hover:bg-amber-500 text-white transition-colors"
          >
            Buy Credits
          </button>
        </div>
      </div>
    )}
    </>
  );
}
